import { useMutation, useQueryClient } from '@tanstack/react-query';

import { SavedFilters } from '@air/api';
import { type SavedFilter, type SavedFiltersListResponse } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getPrivateSavedSearchKey, isPrivateSavedSearchKey } from '~/components/v2/hooks/queries/usePrivateSavedSearch';
import { isPrivateSavedSearchListKey } from '~/components/v2/hooks/queries/usePrivateSavedSearchList';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { createEmptyListResponseStructure } from '~/utils/ApiUtils';
import { getSavedFilterKey } from './useGetSavedFilter';
import { getSavedFiltersKey } from './useGetSavedFilters';

export const useDeleteSavedFilter = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();

  const deleteSavedFilter = useMutation({
    mutationFn: (savedFilterId: string) => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      return SavedFilters.deleteSavedFilter({ workspaceId, savedFilterId });
    },
    // When mutate is called:
    onMutate: async (savedFilterId) => {
      if (!currentWorkspace?.id) {
        return;
      }

      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: getSavedFiltersKey(currentWorkspace.id) });

      // Snapshot the previous value
      const previousSavedFilters = queryClient.getQueryData<SavedFiltersListResponse>(
        getSavedFiltersKey(currentWorkspace.id),
      );

      /** Remove the individual saved filter from cache */
      queryClient.setQueryData(getSavedFilterKey(savedFilterId), undefined);
      queryClient.setQueryData(getPrivateSavedSearchKey({ id: savedFilterId }), undefined);

      /** Remove the saved filter from list cache */
      queryClient.setQueryData<SavedFiltersListResponse>(getSavedFiltersKey(currentWorkspace.id), (cache) =>
        cache
          ? {
              ...cache,
              data: cache.data.filter((sv) => sv.id !== savedFilterId),
            }
          : createEmptyListResponseStructure(),
      );

      // ------------------------------------------------------------
      // @TODO - Remove everything above this post UI2
      // ------------------------------------------------------------

      queryClient.cancelQueries({ predicate: (query) => isPrivateSavedSearchListKey(query.queryKey) });

      const previousPrivateSavedSearchList = queryClient.getQueriesData<SavedFiltersListResponse>({
        predicate: (query) => isPrivateSavedSearchListKey(query.queryKey),
      });

      previousPrivateSavedSearchList.forEach((query) => {
        queryClient.setQueryData<SavedFiltersListResponse>(query[0], (cache) =>
          cache
            ? {
                ...cache,
                data: cache.data.filter((sv) => sv.id !== savedFilterId),
              }
            : createEmptyListResponseStructure(),
        );
      });

      queryClient.cancelQueries({ predicate: (query) => isPrivateSavedSearchKey(query.queryKey) });

      const previousPrivateSavedSearch = queryClient.getQueriesData<SavedFilter>({
        predicate: (query) => isPrivateSavedSearchKey(query.queryKey) && query.queryKey[1].id === savedFilterId,
      });

      previousPrivateSavedSearch.forEach((query) => {
        queryClient.setQueryData<SavedFilter | undefined>(query[0], undefined);
      });

      // Return a context object with the snapshotted value
      return { previousSavedFilters, previousPrivateSavedSearchList, previousPrivateSavedSearch };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (query) => isPrivateSavedSearchKey(query.queryKey) });
      queryClient.invalidateQueries({ predicate: (query) => isPrivateSavedSearchListKey(query.queryKey) });
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (error, savedFilterId, context) => {
      if (!currentWorkspace?.id) {
        return;
      }

      reportErrorToBugsnag({
        error,
        context: 'Unable to delete saved filter',
        metadata: {
          key: 'data',
          data: { savedFilterId, workspaceId: currentWorkspace?.id },
        },
      });

      queryClient.setQueryData(getSavedFiltersKey(currentWorkspace.id), context?.previousSavedFilters);

      context?.previousPrivateSavedSearchList.forEach((query) => {
        queryClient.setQueryData<SavedFiltersListResponse>(...query);
      });

      context?.previousPrivateSavedSearch.forEach((query) => {
        queryClient.setQueryData<SavedFilter | undefined>(...query);
      });
    },
  });

  return {
    deleteSavedFilter,
  };
};
